import React from "react";
import PropTypes from "prop-types";
import { Typography, Box } from "@material-ui/core";
import { COLORS } from "../constants";
import { serviceDisplayName, resourceDisplayName } from "../utils";
import { SectionHeading, Badge } from "./UI";
import { useStyles } from "../views/wizard-view.style";

export function WizardReviewStep({ wizardSelectedTemplate, wizardLeafs, wizardMode, servicesRef, resourcesRef }) {
  const classes = useStyles();
  const services = wizardSelectedTemplate?.services ?? [];
  const resources = wizardSelectedTemplate?.resources ?? [];
  return (
    <Box>
      <SectionHeading>Review &amp; Confirm</SectionHeading>
      <Box className={classes.reviewCard}>
        <Box className={classes.reviewGrid}>
          <Box>
            <Typography className={classes.reviewLabel}>Template</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{wizardSelectedTemplate?.name}</Typography>
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              {wizardSelectedTemplate?.services.length} service{wizardSelectedTemplate?.services.length === 1 ? "" : "s"}, {wizardSelectedTemplate?.resources.length} resource{wizardSelectedTemplate?.resources.length === 1 ? "" : "s"}
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.reviewLabel}>Locations</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {wizardLeafs.length} target{wizardLeafs.length === 1 ? "" : "s"}
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.reviewLabel}>Mode</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {wizardMode === "merge" ? "Merge" : "Overwrite"}
            </Typography>
          </Box>
        </Box>
        <Typography className={classes.reviewSectionLabel}>Services:</Typography>
        <Box className={classes.reviewBadges}>
          {services.map((service) => (
            <Badge key={service.serviceId ?? service._key} type="tag">
              {serviceDisplayName(service, servicesRef) ?? `Service ${service.serviceId}`}
            </Badge>
          ))}
          {services.length === 0 && (
            <Typography variant="caption" sx={{ color: COLORS.muted }}>None</Typography>
          )}
        </Box>
        <Typography className={classes.reviewSectionLabel}>Resources:</Typography>
        <Box className={classes.reviewBadges}>
          {resources.map((resource) => (
            <Badge key={resource.resourceId ?? resource._key} type="tag">
              {resourceDisplayName(resource, resourcesRef) ?? `Resource ${resource.resourceId}`}
            </Badge>
          ))}
          {resources.length === 0 && (
            <Typography variant="caption" sx={{ color: COLORS.muted }}>None</Typography>
          )}
        </Box>
        <Box
          className={classes.reviewWarning}
          sx={{
            background: wizardMode === "overwrite" ? COLORS.warnLt : COLORS.infoLt,
            color: wizardMode === "overwrite" ? COLORS.warnDark : COLORS.info,
          }}
        >
          {wizardMode === "overwrite" && (
            <Typography variant="caption">
              Overwrite will remove all direct configs at target locations before applying the template.
            </Typography>
          )}
          {wizardMode !== "overwrite" && (
            <Typography variant="caption">
              Merge will add new entries and update overlapping ones. Non-conflicting entries are preserved.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

const wizardSelectedTemplateShape = PropTypes.shape({
  name: PropTypes.string,
  services: PropTypes.array,
  resources: PropTypes.array,
});

WizardReviewStep.propTypes = {
  wizardSelectedTemplate: wizardSelectedTemplateShape,
  wizardLeafs: PropTypes.array,
  wizardMode: PropTypes.string,
  servicesRef: PropTypes.array,
  resourcesRef: PropTypes.array,
};

WizardReviewStep.defaultProps = {
  wizardSelectedTemplate: null,
  wizardLeafs: [],
  wizardMode: "merge",
  servicesRef: [],
  resourcesRef: [],
};
